
import { Settings, Bell, BellDot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { ConsentService } from '@/services/consentService';
import { UserMenu } from './UserMenu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useCompany } from '@/hooks/useCompany';
import { isNotificationsEnabled, isSettingsEnabled } from '@/config/features';

type Notification = {
  id: string;
  type: 'approval' | 'payment' | 'overdue';
  count: number;
  route: string;
};

// The sentence for each kind, in `consultantShell.topbar.notifications`; it is chosen at render time so it follows the language.
const NOTIFICATION_TEXT_KEY = {
  approval: 'invoicesPendingApproval',
  payment: 'paymentsToSend',
  overdue: 'paymentsOverdue',
} as const;

const Topbar = () => {
  const t = useTranslations('consultantShell.topbar');
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(true); // New state to track unread status
  const { company, loading } = useCompany();
  
  // Fetch notifications data
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        // Here we would normally fetch from an API
        // For now using mock data based on what might be pending in the system
        const invoiceService = ServiceRegistry.getInvoiceService();
        
        // Get pending invoices that need approval
        const pendingInvoices = await invoiceService.getPendingInvoices();
        
        const newNotifications: Notification[] = [];
        
        if (pendingInvoices.length > 0) {
          newNotifications.push({
            id: 'pending-approvals',
            type: 'approval',
            count: pendingInvoices.length,
            route: '/account-payables/approvals'
          });
        }
        
        // Add mock notifications for demonstration
        newNotifications.push({
          id: 'pending-payments',
          type: 'payment',
          count: 2,
          route: '/account-payables/payments'
        });
        
        newNotifications.push({
          id: 'overdue-payments',
          type: 'overdue',
          count: 1,
          route: '/account-receivables'
        });
        
        setNotifications(newNotifications);
        
        // If we have new notifications, set unread to true
        if (newNotifications.length > 0) {
          setHasUnread(true);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    
    fetchNotifications();
    
    // Set up an interval to periodically check for new notifications
    const intervalId = setInterval(fetchNotifications, 60000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, []);
  
  const totalNotifications = notifications.reduce((sum, notification) => sum + notification.count, 0);
  
  const handleNotificationClick = (route: string) => {
    setIsOpen(false);
    navigate(route);
  };
  
  // New function to mark notifications as read
  const handleMarkAllAsRead = () => {
    setHasUnread(false); // Just mark as read without clearing notifications
    toast.success(t('notifications.markedAllRead'));
  };
  
  return (
    <div className="h-16 border-b flex items-center justify-between px-6 bg-background fixed top-0 z-40" style={{ left: '64px', right: '0' }}>
      <div className="flex-1 flex items-center">
        {loading ? (
          <div className="text-sm text-muted-foreground">{t('loadingCompany')}</div>
        ) : company ? (
          <div className="text-sm font-medium" data-ph-mask>{company.name}</div>
        ) : (
          <div className="text-sm text-muted-foreground">{t('noCompany')}</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isNotificationsEnabled() && (
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground relative"
                aria-label={t('notifications.title')}
              >
                {totalNotifications > 0 && hasUnread ? (
                  <>
                    <BellDot size={20} className="text-current" />
                    <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center">
                      <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                    </span>
                  </>
                ) : (
                  <Bell size={20} />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="border-b p-3">
                <div className="font-medium">{t('notifications.title')}</div>
              </div>
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {t('notifications.empty')}
                </div>
              ) : (
                <div className="max-h-80 overflow-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="flex items-center justify-between p-3 hover:bg-muted cursor-pointer"
                      onClick={() => handleNotificationClick(notification.route)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{t(`notifications.${NOTIFICATION_TEXT_KEY[notification.type]}`, { count: notification.count })}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('notifications.clickToView')}
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant={notification.type === 'overdue' ? 'destructive' : 'default'}
                      >
                        {notification.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              {notifications.length > 0 && (
                <div className="border-t p-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-center text-xs"
                    onClick={handleMarkAllAsRead}
                  >
                    {t('notifications.markAllRead')}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}

        {isSettingsEnabled() && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t('settings')}
          >
            <Settings size={20} />
          </Button>
        )}
        <UserMenu />
      </div>
    </div>
  );
};

export default Topbar;
